/**
 * Estado de un listado enlazable. Vista, búsqueda, filtros, orden y página
 * viven en la query string. La selección no: no tiene sentido compartirla.
 */

export type SortDir = 'asc' | 'desc';

export interface ResourceIndexQuery {
    view: string;
    q: string;
    page: number;
    sort: string | null;
    dir: SortDir | null;
    filters: Record<string, string>;
}

export interface ResourceIndexUrlOptions {
    views: readonly string[];
    defaultView: string;
    filterKeys: readonly string[];
}

const VIEW_KEY = 'view';
const Q_KEY = 'q';
const PAGE_KEY = 'page';
const SORT_KEY = 'sort';
const DIR_KEY = 'dir';

export function parseResourceIndexQuery(
    params: URLSearchParams,
    options: ResourceIndexUrlOptions,
): ResourceIndexQuery {
    const rawView = params.get(VIEW_KEY);
    const view = rawView && options.views.includes(rawView) ? rawView : options.defaultView;
    const rawPage = Number(params.get(PAGE_KEY));
    const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
    const sort = params.get(SORT_KEY)?.trim() || null;
    const dirRaw = params.get(DIR_KEY);
    const dir: SortDir | null = !sort ? null : dirRaw === 'desc' ? 'desc' : 'asc';
    const filters: Record<string, string> = {};
    for (const key of options.filterKeys) {
        const value = params.get(key)?.trim();
        if (value) filters[key] = value;
    }
    return { view, q: params.get(Q_KEY) ?? '', page, sort, dir, filters };
}

export function writeResourceIndexQuery(
    current: URLSearchParams,
    query: ResourceIndexQuery,
    options: ResourceIndexUrlOptions,
): URLSearchParams {
    const next = new URLSearchParams(current);
    for (const key of [VIEW_KEY, Q_KEY, PAGE_KEY, SORT_KEY, DIR_KEY, ...options.filterKeys]) {
        next.delete(key);
    }
    if (query.view !== options.defaultView && options.views.includes(query.view)) {
        next.set(VIEW_KEY, query.view);
    }
    const q = query.q.trim();
    if (q) next.set(Q_KEY, q);
    if (query.page > 1) next.set(PAGE_KEY, String(query.page));
    if (query.sort) {
        next.set(SORT_KEY, query.sort);
        next.set(DIR_KEY, query.dir === 'desc' ? 'desc' : 'asc');
    }
    for (const key of options.filterKeys) {
        const value = query.filters[key]?.trim();
        if (value) next.set(key, value);
    }
    return next;
}

export function toggleSelectedId(selected: readonly string[], id: string): string[] {
    return selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id];
}

/** Marca o desmarca solo las filas de la página. El resto de la selección se queda. */
export function selectPageIds(
    selected: readonly string[],
    pageIds: readonly string[],
    checked: boolean,
): string[] {
    if (!checked) {
        const drop = new Set(pageIds);
        return selected.filter((id) => !drop.has(id));
    }
    const next = new Set(selected);
    for (const id of pageIds) next.add(id);
    return [...next];
}

export function pageSelectionState(
    selected: readonly string[],
    pageIds: readonly string[],
): { all: boolean; some: boolean } {
    if (pageIds.length === 0) return { all: false, some: false };
    const picked = new Set(selected);
    const hits = pageIds.filter((id) => picked.has(id)).length;
    return { all: hits === pageIds.length, some: hits > 0 && hits < pageIds.length };
}
